export interface TooltipPosition {
  x: number
  y: number
}

export interface TooltipRow {
  label: string
  value: string | number
  color?: 'default' | 'green' | 'red' | 'gray'
}

interface TooltipProps {
  title?: string
  rows: TooltipRow[]
  position: TooltipPosition
  visible: boolean
}

function formatValue(value: string | number): string {
  if (typeof value === 'number') {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`
    if (value >= 1000) return `${(value / 1000).toFixed(1)}k`
    return value.toLocaleString()
  }
  return value
}

function getColorClass(color: TooltipRow['color']): string {
  switch (color) {
    case 'green':
      return 'text-green-400'
    case 'red':
      return 'text-red-400'
    case 'gray':
      return 'text-gray-400'
    default:
      return 'text-white'
  }
}

export function Tooltip({ title, rows, position, visible }: TooltipProps) {
  if (!visible) return null

  return (
    <div
      className="fixed z-50 bg-gray-900 text-white text-xs rounded-lg shadow-lg py-2 px-3 pointer-events-none transition-all duration-150 ease-out"
      style={{
        left: position.x,
        top: position.y - 12,
        transform: 'translate(-50%, -100%)',
        opacity: visible ? 1 : 0,
      }}
    >
      {title && (
        <div className="font-medium mb-1.5 border-b border-gray-700 pb-1.5">
          {title}
        </div>
      )}
      <div className="space-y-1">
        {rows.map((row, index) => (
          <div key={index} className="flex items-center justify-between gap-4">
            <span className="text-gray-400">{row.label}</span>
            <span className={`font-medium ${getColorClass(row.color)}`}>
              {formatValue(row.value)}
            </span>
          </div>
        ))}
      </div>
      {/* Arrow */}
      <div className="absolute left-1/2 bottom-0 w-2 h-2 bg-gray-900 transform -translate-x-1/2 translate-y-1/2 rotate-45" />
    </div>
  )
}

// Utility functions for formatting tooltip values
export function formatCurrency(amount: number): string {
  if (amount >= 1000) return `$${(amount / 1000).toFixed(1)}k`
  return `$${amount.toFixed(0)}`
}

export function formatPercentage(value: number): string {
  return `${value.toFixed(1)}%`
}

export function formatCount(count: number): string {
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`
  if (count >= 1000) return `${(count / 1000).toFixed(1)}k`
  return count.toLocaleString()
}
